import _ from "lodash";
import {
  COLUMN_HEADER_PROPERTIES,
  CUSTOM_SUFFIX,
  DATA_GRID_CELL_TYPES,
  DATA_GRID_COLUMN_TYPES,
  DATA_SOURCE_TYPES,
  DEFAULT_COLUMNS,
  DROPDOWN_COLUMN_CONFIG,
  PLACEHOLDERS_BY_COLUMN_ID,
} from "../constants";
import { hasError } from "./validationUtils";

export const scrollbarWidth = (() => {
  const dummyElement = document.createElement("div");
  dummyElement.style.width = "100px";
  dummyElement.style.height = "100px";
  dummyElement.style.overflow = "scroll";
  dummyElement.style.visibility = "hidden";

  document.body.appendChild(dummyElement);
  const scrollbarWidth = dummyElement.offsetWidth - dummyElement.clientWidth;
  document.body.removeChild(dummyElement);

  return scrollbarWidth;
})();

export const getCurrentColumns = ({ isEditMode }) => {
  if (isEditMode) return DEFAULT_COLUMNS;

  return _.without(
    DEFAULT_COLUMNS,
    DATA_GRID_COLUMN_TYPES.MODIFY_DISPLAY_SEQUENCE,
    DATA_GRID_COLUMN_TYPES.DELETE
  );
};

export const getDataGridHeaders = ({
  parentContainerWidth,
  t,
  isEditMode,
  onIconClick,
  columns,
}) => {
  const properties = COLUMN_HEADER_PROPERTIES(t);
  let zeroWidthColumns = 0;

  const headers = _.map(columns, id => {
    const headerConfig = properties[id] || {};
    if (!headerConfig.width) zeroWidthColumns += 1;

    const { title, width = 0, suffixIcon } = headerConfig;

    const result = {
      id,
      title,
      width,
    };

    if (isEditMode && suffixIcon) {
      result.suffixIcon = {
        ...suffixIcon,
        onIconClick: () => onIconClick(id),
      };
    }

    return result;
  });

  const viewportWidth = parentContainerWidth - 2 - scrollbarWidth;
  const totalDefinedWidth = _.sumBy(headers, "width");
  const remainingWidth = viewportWidth - totalDefinedWidth;

  // Distribute remaining width among zero-width columns
  if (zeroWidthColumns > 0 && remainingWidth > 0) {
    const additionalWidthPerColumn = remainingWidth / zeroWidthColumns;

    return _.map(headers, header =>
      header.width === 0
        ? { ...header, width: additionalWidthPerColumn }
        : header
    );
  }

  return headers;
};

const updateRowChanges = ({ rowId, changes, added, setAdded, setUpdated }) => {
  const setter = added.has(rowId) ? setAdded : setUpdated;
  setter(prev => {
    prev.set(rowId, changes);
    return prev;
  });
};

const modifyDisplaySequence = ({
  setUpdated,
  setAdded,
  updated,
  added,
  data,
  grade,
  displaySequence,
  targetIndex,
  setFocusCell,
}) => () => {
  const targetRow = _.get(data, targetIndex, null);
  if (!targetRow) return;

  const targetRowId = _.get(targetRow, "grade.id");
  const currentRowId = _.get(grade, "id");

  const getRowChanges = rowId => updated.get(rowId) || added.get(rowId) || {};

  const targetRowChanges = getRowChanges(targetRowId);
  const currentRowChanges = getRowChanges(currentRowId);

  currentRowChanges.displaySequence = targetRow.displaySequence;
  targetRowChanges.displaySequence = displaySequence;

  const commonParams = { added, setAdded, setUpdated };

  updateRowChanges({
    rowId: targetRowId,
    changes: targetRowChanges,
    ...commonParams,
  });
  updateRowChanges({
    rowId: currentRowId,
    changes: currentRowChanges,
    ...commonParams,
  });

  setFocusCell({
    rowId: currentRowId,
    colId: DATA_GRID_COLUMN_TYPES.MODIFY_DISPLAY_SEQUENCE,
  });

  setTimeout(() => {
    setFocusCell({
      rowId: null,
      colId: null,
    });
  });
};

const createSingleGroupOptions = ({ title, options }) => {
  if (_.isEmpty(options)) {
    return [];
  }

  return [
    {
      id: "group",
      label: title,
      isDisabled: true,
      options,
    },
  ];
};

const createValidInputOptions = ({ data, labelKey = "name", title }) => {
  const options = _.map(data, item => ({
    id: _.get(item, "id"),
    label: _.get(item, labelKey),
  }));

  return {
    options: createSingleGroupOptions({ title, options }),
  };
};

const getGradingPeriodsValidInput = ({
  isCustom,
  customGradingPeriodsValidInput,
  gradingPeriods,
  schoolSourceId,
  academicYearSourceId,
  toddleDefinedGradingPeriodsLabel,
}) => {
  if (isCustom) return customGradingPeriodsValidInput;

  const options = _.chain(gradingPeriods)
    .filter(({ source }) => {
      const curriculumProgramId = _.get(source, "curriculumProgram.id");
      const academicYearId = _.get(source, "academicYear.id");
      return (
        curriculumProgramId === schoolSourceId &&
        academicYearId === academicYearSourceId
      );
    })
    .map(({ id, name }) => ({ id, label: name }))
    .value();

  return {
    options: createSingleGroupOptions({
      title: toddleDefinedGradingPeriodsLabel,
      options,
    }),
  };
};

const createDropdownColumn = ({
  value,
  placeholder,
  isCustom,
  isEditMode,
  columnId,
  validInputs,
  hasError,
}) => ({
  cellType: DATA_GRID_CELL_TYPES.DROPDOWN,
  value,
  placeholder,
  showDropdownIcon: isCustom && isEditMode,
  isEditable: isCustom && isEditMode,
  isDisabled: isEditMode && !isCustom,
  columnId,
  validInputs,
  hasError,
  addNewOptionEnabled: isCustom,
  checkboxAlignment: "right",
  maxMenuHeight: 188,
  styles: {
    color: isEditMode && !isCustom ? "#8f8f8f" : undefined,
  },
});

const buildColumnConfig = params => {
  const {
    columnId,
    grade,
    gradingPeriods,
    isHidden,
    displaySequence,
    isCustom,
    isEditMode,
    customSuffix,
    placeholdersByColId,
    gradingPeriodsValidInput,
    errors,
    setRemoved,
    setUpdated,
    setAdded,
    added,
    updated,
    data,
    index,
    setFocusCell,
    disabledTooltip,
  } = params;

  const gradeId = _.get(grade, "id");
  const showError = hasError({ isEditMode, errors, gradeId, columnId });

  switch (columnId) {
    case DATA_GRID_COLUMN_TYPES.GRADE_LEVEL:
      return {
        cellType: DATA_GRID_CELL_TYPES.LABEL,
        value: _.get(grade, "name") || "-",
        columnId,
        suffix: {
          isAlignedToRightEnd: false,
          items: isCustom ? customSuffix : [],
        },
      };

    case DATA_GRID_COLUMN_TYPES.ASSOCIATED_SCHOOL:
    case DATA_GRID_COLUMN_TYPES.ACADEMIC_YEARS: {
      const config = DROPDOWN_COLUMN_CONFIG[columnId];
      const entity = params[config.getValueFrom];
      const validInputs = params[config.validInputsKey];

      return createDropdownColumn({
        value: entity
          ? [{ id: entity.id, label: entity[config.labelKey] }]
          : [],
        placeholder: placeholdersByColId[columnId],
        isCustom,
        isEditMode,
        columnId,
        validInputs,
        hasError: showError,
      });
    }

    case DATA_GRID_COLUMN_TYPES.GRADING_PERIODS:
      return {
        cellType: DATA_GRID_CELL_TYPES.TAG,
        value: _.map(gradingPeriods, ({ gradingPeriod }) => ({
          id: gradingPeriod.id,
          label: gradingPeriod.name,
        })),
        placeholder: placeholdersByColId[columnId],
        showDropdownIcon: isEditMode,
        isEditable: isEditMode,
        columnId,
        validInputs: gradingPeriodsValidInput,
        isMulti: true,
        isSelectAllEnabled: false,
        addNewOptionEnabled: isCustom,
        hasError: showError,
        isCustom,
        checkboxAlignment: "right",
        maxMenuHeight: 188,
      };

    case DATA_GRID_COLUMN_TYPES.SHOW_IN_TRANSCRIPT:
      return {
        cellType: DATA_GRID_CELL_TYPES.TOGGLE,
        value: !isHidden,
        columnId,
        isEditable: isEditMode,
      };

    case DATA_GRID_COLUMN_TYPES.MODIFY_DISPLAY_SEQUENCE: {
      const modifySequenceParams = {
        setUpdated,
        setAdded,
        added,
        updated,
        data,
        grade,
        displaySequence,
        setFocusCell,
      };

      return {
        cellType: DATA_GRID_CELL_TYPES.ICON,
        value: [
          {
            name: "UpArrowOutlined",
            spaceAfter: 20,
            onIconClick: modifyDisplaySequence({
              ...modifySequenceParams,
              targetIndex: index - 1,
            }),
            isDisabled: index === 0,
          },
          {
            name: "DownArrowOutlined",
            onIconClick: modifyDisplaySequence({
              ...modifySequenceParams,
              targetIndex: index + 1,
            }),
            isDisabled: index === _.size(data) - 1,
          },
        ],
        columnId,
        isDisabled: !isEditMode,
      };
    }

    case DATA_GRID_COLUMN_TYPES.DELETE:
      return {
        cellType: DATA_GRID_CELL_TYPES.ICON,
        value: "DeleteOutlined",
        columnId,
        isDisabled: !isEditMode || !isCustom,
        tooltip: !isEditMode || !isCustom ? disabledTooltip : null,
        onIconClick: ({ rowId }) => {
          const setter = added.has(rowId) ? setAdded : setRemoved;
          setter(prev => {
            added.has(rowId) ? prev.delete(rowId) : prev.add(rowId);
          });
        },
      };

    default:
      return {
        cellType: DATA_GRID_CELL_TYPES.SKELETON,
        columnId,
      };
  }
};

export const getDataGridData = ({
  data,
  t,
  academicYears,
  gradingPeriods,
  customGradingPeriods,
  schools,
  isEditMode,
  setRemoved,
  setUpdated,
  setAdded,
  added,
  updated,
  errors,
  columns: columnsOrder,
  setFocusCell,
}) => {
  const placeholdersByColId = PLACEHOLDERS_BY_COLUMN_ID(t);
  const customSuffix = CUSTOM_SUFFIX(t);
  const customSchoolLabel = t("common:custom_label", {
    label: t("common:label_lowercase", {
      label: t("common:school_plural"),
    }),
  });
  const customAcademicYearLabel = t("common:custom_label", {
    label: t("common:label_lowercase", {
      label: t("common:academic_year_plural"),
    }),
  });
  const customGradingPeriodsLabel = t("common:custom_label", {
    label: t("common:label_lowercase", {
      label: t("common:grading_period_plural"),
    }),
  });
  const toddleDefinedGradingPeriodsLabel = t(
    "transcript:toddle_defined_label",
    {
      label: t("common:label_lowercase", {
        label: t("common:grading_period_plural"),
      }),
    }
  );
  const disabledTooltip = t(
    "transcript:disabled_configure_grade_level_tooltip"
  );

  const staticParams = {
    isEditMode,
    customSuffix,
    placeholdersByColId,
    schoolValidInput: createValidInputOptions({
      data: schools,
      title: customSchoolLabel,
    }),
    academicYearValidInput: createValidInputOptions({
      data: academicYears,
      labelKey: "label",
      title: customAcademicYearLabel,
    }),
    customGradingPeriodsValidInput: createValidInputOptions({
      data: customGradingPeriods,
      title: customGradingPeriodsLabel,
    }),
    setRemoved,
    setUpdated,
    setAdded,
    added,
    updated,
    errors,
    disabledTooltip,
  };

  return _.map(data, (currentGrade, index) => {
    const {
      dataSource,
      isHidden,
      academicYear,
      grade,
      gradingPeriods: gradeGradingPeriods,
      school,
      displaySequence,
    } = currentGrade;

    const isCustom = dataSource === DATA_SOURCE_TYPES.CUSTOM;
    const schoolSourceId = _.get(school, "sourceId");
    const academicYearSourceId = _.get(academicYear, "sourceId");

    const gradingPeriodsValidInput = getGradingPeriodsValidInput({
      isCustom,
      customGradingPeriodsValidInput:
        staticParams.customGradingPeriodsValidInput,
      gradingPeriods,
      schoolSourceId,
      academicYearSourceId,
      toddleDefinedGradingPeriodsLabel,
    });

    const columnParams = {
      ...staticParams,
      grade,
      school,
      academicYear,
      gradingPeriods: gradeGradingPeriods,
      isHidden,
      displaySequence,
      isCustom,
      gradingPeriodsValidInput,
      data,
      index,
      setFocusCell,
    };

    const columns = _.map(columnsOrder, columnId =>
      buildColumnConfig({ ...columnParams, columnId })
    );

    return {
      rowId: _.get(grade, "id"),
      columns,
    };
  });
};
